import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../lib/firebase";
import {
  completeMagicLink,
  getPendingEmail,
  isMagicLinkUrl,
} from "../../lib/emailLink";
import { Spinner } from "../../components/shared/Spinner";

// Landing page for the Firebase email-link callback. Triggered when a user
// clicks the magic link in their inbox. We confirm the URL is a real auth
// link, look up the email they typed when requesting it, and finish the
// sign-in. After Firebase sets the auth user, useAuth's onAuthStateChanged
// handles the rest (ghost-user check + dashboard redirect).
//
// Edge case: if the user clicks the link on a different device, the email
// is not in localStorage. We then prompt for it.

type State =
  | { kind: "completing" }
  | { kind: "needs_email" }
  | { kind: "error"; message: string }
  | { kind: "done" };

export function EmailLinkComplete() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "completing" });
  const [email, setEmail] = useState("");
  // useRef + ran flag avoids running the sign-in twice in React StrictMode.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!auth) {
      setState({ kind: "error", message: "Firebase no está configurado." });
      return;
    }
    if (!isMagicLinkUrl(auth, window.location.href)) {
      setState({
        kind: "error",
        message: "Enlace inválido o caducado. Vuelve a solicitar uno desde la pantalla de login.",
      });
      return;
    }
    const stored = getPendingEmail();
    if (!stored) {
      setState({ kind: "needs_email" });
      return;
    }
    finish(stored);
  }, []);

  async function finish(emailToUse: string) {
    if (!auth) return;
    setState({ kind: "completing" });
    try {
      await completeMagicLink(auth, window.location.href, emailToUse);
      setState({ kind: "done" });
      // useAuth picks up the new user via onAuthStateChanged. Honor the
      // optional `next` redirect (e.g. invite flow lands back at
      // /accept?token=... to complete the redeem); fall back to home.
      const nextParam = new URLSearchParams(window.location.search).get("next");
      const safeNext = nextParam && nextParam.startsWith("/") ? nextParam : "/";
      navigate(safeNext, { replace: true });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "No se pudo completar el acceso.",
      });
    }
  }

  if (state.kind === "completing" || state.kind === "done") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Spinner />
      </div>
    );
  }

  if (state.kind === "needs_email") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-bg">
        <div className="max-w-sm w-full bg-surface border border-border rounded-2xl p-8">
          <h1 className="text-xl font-black text-ink mb-2">Confirma tu email</h1>
          <p className="text-sm text-ink-2 mb-4">
            Has abierto el enlace en un dispositivo distinto al que lo solicitó.
            Introduce el email al que se envió para completar el acceso.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@empresa.com"
            autoComplete="email"
            className="w-full rounded-full px-4 py-2.5 text-sm focus:outline-none mb-3"
            style={{
              backgroundColor: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "#0f0f0f",
            }}
          />
          <button
            type="button"
            onClick={() => email.includes("@") && finish(email)}
            disabled={!email.includes("@")}
            className="w-full rounded-full px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#111111", color: "#ffffff" }}
          >
            Completar acceso
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-bg">
      <div className="max-w-sm w-full bg-surface border border-border rounded-2xl p-8 text-center">
        <h1 className="text-xl font-black text-ink mb-2">No pudimos entrar</h1>
        <p className="text-sm text-ink-2 mb-4">{state.message}</p>
        <button
          type="button"
          onClick={() => navigate("/", { replace: true })}
          className="text-sm underline text-ink-2"
        >
          Volver al login
        </button>
      </div>
    </div>
  );
}
